import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { LoginTypes } from 'shared-library';
import { CookieService } from 'ngx-cookie-service';
import { Component, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CoreSeoPages } from '@app/store/enums/core-seo-pages';
import * as AuthActions from '../../store/actions/auth.actions';
import { StateStatuses } from '@app/store/enums/state-statuses.enum';
import { AuthDataForm } from '@app/store/models/auth-data.interface';
import { CoreService } from '@app/modules/core/services/core.service';
import { selectAuthState } from '../../store/selectors/auth.selectors';
import { SessionInfo } from '@app/store/models/session-info.interface';
import { AuthState, getAuthState } from '../../store/state/auth.states';
import { FunctionsService } from '@app/modules/core/services/functions.service';
import { SeoEventNames, SeoService } from '@app/modules/core/services/seo.service';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

@Component({
  selector: 'app-auth-signup',
  templateUrl: './auth-signup.component.html',
  styleUrls: ['./../auth.global.scss', './auth-signup.component.scss']
})
export class AuthSignupComponent implements OnDestroy {

  error = ""
  success = ""

  session_info: SessionInfo
  signup_form: FormGroup<AuthDataForm>
  private subscriptions: Subscription[] = []

  state_statuses = {
    FAILED: StateStatuses.FAILED,
    PENDING: StateStatuses.PENDING,
    SUCCESS: StateStatuses.SUCCESS,
    PROCESSING: StateStatuses.PROCESSING,
  }
  auth_types = {
    EMAIL: LoginTypes.EMAIL,
    GOOGLE: LoginTypes.GOOGLE,
    // FACEBOOK: LoginTypes.FACEBOOK,
  }

  auth_state: AuthState;
  authState$ = this.store.select(selectAuthState);

  constructor(
    private store: Store,
    private seo: SeoService,
    private formBuilder: FormBuilder,
    private coreService: CoreService,
    private cookieService: CookieService,
    private functionsService: FunctionsService
  ) {

    this.subscriptions.push(this.coreService.session_dataObservable.subscribe(si => this.session_info = si))
    this.subscriptions.push(
      this.authState$.subscribe(ss => {
        const signed_up = !this.auth_state?.responses?.signup?.success && ss.responses.signup?.success
        this.auth_state = ss
        if (!this.signup_form || signed_up) {
          if (signed_up) {
            this.success = `An account confirmation email has been sent to ${ss.data.email}`
          }
          this.fillFormData();
        }

        if (!ss.responses.signup?.success && ss.responses.signup?.error) {
          this.error = ss.responses.signup.error
        }
      })
    )

    this.subscriptions.push(this.signup_form.get("password").valueChanges.subscribe(vv => this.error = this.success = ""))
    this.subscriptions.push(this.signup_form.get("cpassword").valueChanges.subscribe(vv => this.error = this.success = ""))
    this.seo.generateMetaTags(CoreSeoPages.SIGN_UP)
  }

  ngOnDestroy() {
    this.subscriptions.map(s => s.unsubscribe())
  }

  async createUser() {
    this.error = ""
    this.success = ""
    this.signup_form.markAllAsTouched()
    // this.seo.eventEmitter(SeoEventNames.UserActions, "signup", "signupButtonClick", {
    //   name: this.signup_form.value.name,
    //   email: this.signup_form.value.email,
    //   timezone: this.signup_form.value.timezone,
    //   referral_id: this.signup_form.value.referral_id,
    //   activating_ots_qr: this.signup_form.value.activating_ots_qr,
    //   auth_type: !this.signup_form.value.id_token ? LoginTypes.EMAIL : LoginTypes.GOOGLE,
    // })
    if (!this.signup_form.valid) return

    this.store.dispatch(AuthActions.authSetStatus({ status: StateStatuses.PROCESSING }))
    this.functionsService.logToConsole(this.signup_form.value)
    this.store.dispatch(AuthActions.signup({
      data: {
        code: "",
        id_token: "",
        name: this.signup_form.value.name,
        email: this.signup_form.value.email,
        mobile: this.signup_form.value.mobile,
        company: this.signup_form.value.company,
        timezone: this.signup_form.value.timezone,
        password: this.signup_form.value.password,
        cpassword: this.signup_form.value.cpassword,
        referral_id: this.signup_form.value.referral_id,
        activating_ots_qr: this.signup_form.value.activating_ots_qr,
        password_strength: this.signup_form.value.password_strength,
        recaptcha_token: await this.coreService.getRecaptchaToken(),
        activation_code_ots_qr: this.signup_form.value.activation_code_ots_qr,
        otp: this.signup_form.value.otp.filter(otp => otp).join("").toUpperCase(),
        auth_type: !this.signup_form.value.id_token ? LoginTypes.EMAIL : LoginTypes.GOOGLE,
      }
    }))
  }

  private async fillFormData() {
    const passwordErrorValidator: ValidatorFn = (control: FormGroup<AuthDataForm>): ValidationErrors | null => {
      const password = control.get('password');
      const repeatPassword = control.get('cpassword');
      return password.value != repeatPassword.value ? { 'passwordError': true } : null;
    };

    const invalidCharacterInName = (): ValidatorFn => {
      return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (!value) return null;
        if (value.split(' ').find((v: string) => this.functionsService.is_valid_url(v) || this.functionsService.isEmail(v))) {
          return { invalidCharacterInName: true };
        }
        return null;
      }
    }
    if (!this.auth_state) {
      this.auth_state = await getAuthState(this.store)
    }

    this.signup_form = this.formBuilder.group({
      company: [this.auth_state.data.company],
      id_token: [this.auth_state.data.id_token],
      timezone: [this.auth_state.data.timezone],
      cpassword: [this.auth_state.data.cpassword],
      auth_type: [this.auth_state.data.auth_type],
      recaptcha_token: [this.auth_state.data.recaptcha_token],
      activating_ots_qr: [this.auth_state.data.activating_ots_qr],
      password_strength: [this.auth_state.data.password_strength],
      password: [this.auth_state.data.password, [Validators.required]],
      activation_code_ots_qr: [this.auth_state.data.activation_code_ots_qr],
      name: [this.auth_state.data.name, [Validators.required, invalidCharacterInName()]],
      referral_id: [this.cookieService.get(environment.referral_cookie_key)],
      email: [this.auth_state.data.email, [Validators.required, Validators.email]],
      mobile: [this.auth_state.data.mobile, [Validators.nullValidator, Validators.pattern(this.functionsService.mobileRegex())]],

      code: [this.auth_state.data.code],
      otp: this.formBuilder.array(["", "", "", "", "", ""].map(otp => this.formBuilder.control(otp))),

    }, { validators: passwordErrorValidator })
  }

}

